import React from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, CheckCircle, XCircle, Clock, AlertTriangle, User, ArrowRight, MapPin, Zap } from 'lucide-react';
import { StatsCard } from '../components/StatsCard';
import { DataTable } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
const jobFeed = [{
  id: 'JOB-101',
  rider: 'John Doe',
  customer: 'Alice Smith',
  status: 'accepted',
  time: '2 min ago',
  response: '5s'
}, {
  id: 'JOB-102',
  rider: 'Mike Ross',
  customer: 'Bob Johnson',
  status: 'rejected',
  time: '5 min ago',
  response: '12s'
}, {
  id: 'JOB-103',
  rider: 'Sarah Connor',
  customer: 'Charlie Brown',
  status: 'missed',
  time: '8 min ago',
  response: '--'
}, {
  id: 'JOB-104',
  rider: 'Bruce Wayne',
  customer: 'David Wilson',
  status: 'assigned',
  time: '10 min ago',
  response: '...'
}, {
  id: 'JOB-105',
  rider: 'Clark Kent',
  customer: 'Eve Davis',
  status: 'accepted',
  time: '12 min ago',
  response: '3s'
}, {
  id: 'JOB-106',
  rider: 'Diana Prince',
  customer: 'Frank Miller',
  status: 'accepted',
  time: '15 min ago',
  response: '8s'
}, {
  id: 'JOB-107',
  rider: 'Barry Allen',
  customer: 'Grace Lee',
  status: 'rejected',
  time: '18 min ago',
  response: '4s'
}, {
  id: 'JOB-108',
  rider: 'Arthur Curry',
  customer: 'Henry Ford',
  status: 'missed',
  time: '20 min ago',
  response: '--'
}];
const riderPerformance = [{
  id: 1,
  name: 'John Doe',
  completed: 145,
  missed: 2,
  rejectionRate: '1.4%',
  score: 98,
  penalties: 0
}, {
  id: 2,
  name: 'Mike Ross',
  completed: 89,
  missed: 5,
  rejectionRate: '5.6%',
  score: 92,
  penalties: 0
}, {
  id: 3,
  name: 'Sarah Connor',
  completed: 12,
  missed: 8,
  rejectionRate: '40.0%',
  score: 45,
  penalties: 2
}, {
  id: 4,
  name: 'Bruce Wayne',
  completed: 320,
  missed: 1,
  rejectionRate: '0.3%',
  score: 99,
  penalties: 0
}, {
  id: 5,
  name: 'Clark Kent',
  completed: 210,
  missed: 0,
  rejectionRate: '0.0%',
  score: 100,
  penalties: 0
}, {
  id: 6,
  name: 'Diana Prince',
  completed: 150,
  missed: 3,
  rejectionRate: '2.0%',
  score: 96,
  penalties: 0
}, {
  id: 7,
  name: 'Barry Allen',
  completed: 98,
  missed: 12,
  rejectionRate: '12.2%',
  score: 78,
  penalties: 1
}, {
  id: 8,
  name: 'Arthur Curry',
  completed: 45,
  missed: 15,
  rejectionRate: '33.3%',
  score: 55,
  penalties: 3
}];
const penalties = [{
  id: 1,
  rider: 'Sarah Connor',
  reason: 'Repeated job rejections',
  type: 'warning',
  date: 'Today, 9:00 AM',
  admin: 'Admin User'
}, {
  id: 2,
  rider: 'Arthur Curry',
  reason: 'No-show for accepted job',
  type: 'fine',
  amount: '₦15.00',
  date: 'Yesterday',
  admin: 'Manager'
}, {
  id: 3,
  rider: 'Barry Allen',
  reason: 'Late arrival > 30 mins',
  type: 'warning',
  date: '2 days ago',
  admin: 'System'
}, {
  id: 4,
  rider: 'Sarah Connor',
  reason: 'Customer complaint: Rude behavior',
  type: 'suspension',
  duration: '24h',
  date: '3 days ago',
  admin: 'Support Lead'
}, {
  id: 5,
  rider: 'Arthur Curry',
  reason: 'Repeated job rejections',
  type: 'warning',
  date: '1 week ago',
  admin: 'System'
}];
export function JobAssignment() {
  const columns = [{
    header: 'Rider',
    accessor: (row: any) => <div className="flex items-center gap-3" data-id="element-595">
          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-596">
            {row.name.charAt(0)}
          </div>
          <span className="font-medium text-slate-900" data-id="element-597">{row.name}</span>
        </div>
  }, {
    header: 'Jobs Completed',
    accessor: 'completed'
  }, {
    header: 'Jobs Missed',
    accessor: 'missed'
  }, {
    header: 'Rejection Rate',
    accessor: (row: any) => {
      const rate = parseFloat(row.rejectionRate);
      const color = rate < 10 ? 'text-green-600' : rate < 25 ? 'text-yellow-600' : 'text-red-600';
      return <span className={`font-bold ${color}`} data-id="element-598">{row.rejectionRate}</span>;
    }
  }, {
    header: 'Performance Score',
    accessor: (row: any) => {
      const score = row.score;
      const color = score >= 90 ? 'bg-green-100 text-green-700' : score >= 70 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
      return <span className={`px-2 py-1 rounded-full text-xs font-bold ${color}`} data-id="element-599">
            {score}/100
          </span>;
    }
  }, {
    header: 'Penalties',
    accessor: 'penalties'
  }];
  return <div className="space-y-6" data-id="element-600">
      {/* Proximity Assignment Info Banner */}
      <div className="bg-gradient-to-r from-famo-gold/10 to-famo-gold/5 border border-famo-gold/20 rounded-xl p-4 flex items-center gap-3" data-id="element-601">
        <div className="w-10 h-10 rounded-full bg-famo-gold/20 flex items-center justify-center flex-shrink-0" data-id="element-602">
          <Zap size={20} className="text-famo-gold" data-id="element-603" />
        </div>
        <div data-id="element-604">
          <h4 className="font-bold text-slate-900 text-sm" data-id="element-605">
            Smart Assignment Algorithm
          </h4>
          <p className="text-xs text-slate-600" data-id="element-606">
            System automatically assigns riders based on proximity to pickup
            location, availability, and performance score
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-607">
        <StatsCard title="Jobs Today" value="1,580" trend={10} icon={ClipboardList} color="blue" delay={0} data-id="element-608" />
        <StatsCard title="Acceptance Rate" value="87%" trend={-2} icon={CheckCircle} color="green" delay={0.1} data-id="element-609" />
        <StatsCard title="Missed Jobs" value="204" trend={8} icon={XCircle} color="red" delay={0.2} data-id="element-610" />
        <StatsCard title="Avg Response Time" value="12s" trend={-15} icon={Clock} color="gold" delay={0.3} data-id="element-611" />
      </div>

      {/* Live Job Feed */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4
    }} className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-612">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-613">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-614"></div>
          Live Job Feed
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-615">
          {jobFeed.map(job => <div key={job.id} className={`p-4 rounded-lg border border-slate-100 bg-slate-50/50 border-l-4 ${job.status === 'accepted' ? 'border-l-green-500' : job.status === 'rejected' ? 'border-l-red-500' : job.status === 'missed' ? 'border-l-yellow-500' : 'border-l-blue-500'}`} data-id="element-616">
              <div className="flex justify-between items-start mb-2" data-id="element-617">
                <span className="text-xs font-mono text-slate-500" data-id="element-618">
                  {job.id}
                </span>
                <span className="text-xs text-slate-400" data-id="element-619">{job.time}</span>
              </div>
              <div className="flex items-center gap-2 mb-1" data-id="element-620">
                <span className="font-medium text-sm text-slate-900" data-id="element-621">
                  {job.rider}
                </span>
                <ArrowRight size={12} className="text-slate-400" data-id="element-622" />
                <span className="text-sm text-slate-600" data-id="element-623">{job.customer}</span>
              </div>
              {job.status === 'assigned' && <div className="mb-2 flex items-center gap-1 text-[10px] text-blue-600" data-id="element-624">
                  <MapPin size={10} data-id="element-625" />
                  <span data-id="element-626">Nearest rider (0.8km)</span>
                </div>}
              <div className="flex justify-between items-center mt-2" data-id="element-627">
                <StatusBadge status={job.status} data-id="element-628" />
                <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-629">
                  <Clock size={10} data-id="element-630" /> {job.response}
                </span>
              </div>
            </div>)}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-631">
        {/* Performance Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="lg:col-span-2" data-id="element-632">
          <DataTable title="Rider Performance Scores" data={riderPerformance} columns={columns} searchPlaceholder="Search riders..." actions={row => <>
                <button className="p-1.5 text-slate-500 hover:text-famo-navy hover:bg-slate-100 rounded transition-colors" title="Adjust Score" data-id="element-634">
                  <ClipboardList size={18} data-id="element-635" />
                </button>
                <button className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors" title="Issue Penalty" data-id="element-636">
                  <AlertTriangle size={18} data-id="element-637" />
                </button>
              </>} data-id="element-633" />
        </motion.div>

        {/* Penalty Log */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 h-fit" data-id="element-638">
          <h3 className="text-lg font-bold text-slate-800 mb-4" data-id="element-639">
            Recent Penalties
          </h3>
          <div className="space-y-4" data-id="element-640">
            {penalties.map(penalty => <div key={penalty.id} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0" data-id="element-641">
                <div className="flex justify-between items-start mb-1" data-id="element-642">
                  <span className="font-medium text-slate-900" data-id="element-643">
                    {penalty.rider}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase ${penalty.type === 'warning' ? 'bg-yellow-100 text-yellow-700' : penalty.type === 'fine' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`} data-id="element-644">
                    {penalty.type}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-1" data-id="element-645">{penalty.reason}</p>
                {penalty.amount && <p className="text-sm font-bold text-red-600 mb-1" data-id="element-646">
                    Fine: {penalty.amount}
                  </p>}
                {penalty.duration && <p className="text-sm font-bold text-slate-600 mb-1" data-id="element-647">
                    Duration: {penalty.duration}
                  </p>}
                <div className="flex justify-between text-xs text-slate-400 mt-2" data-id="element-648">
                  <span data-id="element-649">By: {penalty.admin}</span>
                  <span data-id="element-650">{penalty.date}</span>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}